import { REHYDRATE } from 'redux-persist/constants'
import { SET_WEBSOCKET_ERROR } from 'actions/websockets'
import { modelReducer, formReducer } from 'react-redux-form'
import { mapLanguageToMoment, getValidLanguage } from 'i18n'
import moment from 'moment-timezone'
import initialState from 'initial-state'
import * as actions from 'actions'
import * as sessionActions from 'actions/session'
import * as httpActions from 'actions/http'
import * as adminActions from 'admin/actions'
import * as videoResponseActions from 'actions/video-response'
import * as analytics from 'analytics/performance'
import opentokReducer from './opentok'
import models from '../models' // This have to be relative import as long as backend and frontend shares modules.
import { addUserDataToIntercom } from 'chat-bots/intercom'
import { getUserPropertiesForAmplitude, getUserMulticompanyBelonging } from 'selectors'

export function generateReducers(models, modelInitialState = {}) {
  const modelReducers = {}
  const formReducers = {}

  Object.keys(models).forEach((name) => {
    modelReducers[name] = modelReducer(name, modelInitialState[name])
    formReducers[`${name}Form`] = formReducer(name, modelInitialState[name])
  })

  return { modelReducers, formReducers }
}

const { modelReducers, formReducers } = generateReducers(models, initialState)

export default {
  global(state = initialState.global, action) {
    let newState = { ...state }

    switch (action.type) {
      case REHYDRATE: {
        const { global: rehydratedState } = action.payload
        if (rehydratedState) {
          rehydratedState.fetching = 0
          if (
            rehydratedState.session.user &&
            rehydratedState.session.user.language
          ) {
            rehydratedState.language = getValidLanguage(
              rehydratedState.session.user.language
            )
          }
          return rehydratedState
        }
        break
      }

      case actions.SET_ACTION_BAR_HEIGHT:
        newState.actionBarHeight = action.payload
        break

      case SET_WEBSOCKET_ERROR:
        newState.websocketError = action.payload
        break

      case actions.FETCHING_SET:
        newState.fetching += action.fetching ? 1 : -1
        break

      case actions.FILE_ADD:
        newState.files[action.key] = action.url
        break

      case actions.FILE_REMOVE:
        delete newState.files[action.key]
        break

      case actions.FILES_REMOVE:
        newState.files = {}
        break

      case actions.SET_SHOW_INTERCOM: {
        newState.showIntercom = action.payload
        break
      }

      case httpActions.HTTP_ERROR:
        newState.httpError = action.status
        break

      case actions.MESSAGE_ADD:
        // We can't mutate state with push or connected component will not see the change
        newState.messages = [
          ...state.messages,
          action.message,
        ]
        break

      case actions.MESSAGE_REMOVE: {
        // We can't mutate state with splice or connected component will not see the change
        newState.messages = state.messages.filter(message => message.id !== action.id)
        break
      }

      case actions.MODAL_TOGGLE:
        newState.modal = state.modal != action.modal ? action.modal : null
        break

      case actions.NAVIGATION_MENU_TOGGLE:
        newState.navigationMenu = state.navigationMenu != action.navigationMenu ? action.navigationMenu : null
        break

      case sessionActions.LOGIN: {
        const { session } = action
        const isMultiCompaniesBelonging = getUserMulticompanyBelonging(session.user)
        newState.session = {
          ...session,
          user: {
            ...session.user,
            isMultiCompaniesBelonging,
          },
        }

        if (action.language && action.language !== session.user.language) {
          newState.language = getValidLanguage(action.language)
        } else {
          newState.language = getValidLanguage(session.user.language)
        }
        moment.locale(mapLanguageToMoment(newState.language))

        if (session.user) {
          const userProperties = getUserPropertiesForAmplitude(session.user)
          analytics.onLogin(session.user.id, userProperties)
        }

        /* global zE*/
        // TODO: remove when intercom is implemented and tested in prod
        if (window.zE) {
          window.zE(() => {
            window.zE.identify({
              name: session.user.name,
              email: session.user.email,
            })
            window.zE.setLocale(
              newState.language === 'en' ? 'en-US' : newState.language
            )
          })
        }
        addUserDataToIntercom(session.user)
        break
      }

      case sessionActions.LOGOUT:
        newState = {
          ...initialState.global,
          files: {},
          language: state.language,
          opentok: {
            ...initialState.global.opentok,
            recordingSessionData: {},
            candidateSessionData: {},
            testSessionData: {},
          },
        }
        break

      case sessionActions.REGION: {
        const { region } = action
        newState.region = {
          host: region.host || '',
          geolocation: region.geolocation || '',
        }
        break
      }

      case actions.LANGUAGE_SET: {
        const language = getValidLanguage(action.language)
        newState.language = language

        // Remove ordinal character from italian and spanish dates
        // (Doesn't work right if locale is set just once)
        moment.locale(mapLanguageToMoment(language))
        moment.locale(mapLanguageToMoment(language), language === 'it' || language === 'es'
          ? { ordinal: '%d' }
          : undefined)
        /* global zE */
        if (window.zE) {
          window.zE(() => {
            window.zE.setLocale(language === 'en' ? 'en-US' : language)
          })
        }
        break
      }

      case actions.CHANGE_GLOBAL_RECORDER: {
        const { recorder } = action.recorder
        newState.recorder = recorder
        break
      }

      case actions.VIDEOPLAYER_UPDATE:
        newState.videoPlayer = action.videoPlayer ? { ...state.videoPlayer, ...action.videoPlayer } : null
        break

      // This is on wrong file because favoriteVideos is saved to global state (as is session)
      case actions.FAVORITE_VIDEOS_UPDATE:
        if (action.context === 'company') {
          newState.session.user.companyId.videos = action.favoriteVideos
        } else {
          newState.session.user.favoriteVideos = action.favoriteVideos
        }
        break

      case actions.SORT:
        newState.sort = { ...state.sort, [action.name]: action.key }
        break

      case actions.SORT_RESET:
        newState.sort[action.name] = initialState.global.sort[action.name]
        break

      case actions.FILTER:
        newState.filter = { ...state.filter, [action.name]: action.value }
        break

      case actions.TOGGLE_EMBEDDED:
        newState.embedded = action.value
        break

      case adminActions.JOYRIDE_RESET:
        newState.session.user.joyride = { reset: !action.payload || !!action.payload.reset }
        break

      case adminActions.JOYRIDE_INTERACTION:
        newState.session.user.joyride = { ...state.session.user.joyride }
        newState.session.user.joyride[action.payload.page] = [
          ...newState.session.user.joyride[action.payload.page] || [],
          ...action.payload.steps,
        ]
        break

      case adminActions.UPDATE_USER_LOCATION:
        newState.session.user = { ...state.session.user, location: action.payload }
        break

      case videoResponseActions.VIDEO_RESPONSE_UPLOADING:
        newState.videoUploads = [
          ...state.videoUploads,
          action.payload.videoUrl,
        ]
        break

      case videoResponseActions.VIDEO_RESPONSE_UPLOAD_FINISHED:
        newState.videoUploads = state.videoUploads.filter(video => video != action.payload.videoUrl)
        break

      case videoResponseActions.RECORDING_TOGGLE:
        newState.recording = action.payload
        break

      case videoResponseActions.OPEN_QUESTION_SET:
        newState.openQuestion = action.payload
        break

      case videoResponseActions.BG_OPTIONS_SET:
        newState.bgOptions = action.payload
        break

      case adminActions.VIDEO_UPDATED: {
        switch (action.payload.selectedLibrary) {
          case 'user':
            newState.session.user.favoriteVideos = newState.session.user.favoriteVideos
              .map(video => video._id !== action.payload.id
                ? video
                : {
                  ...video,
                  title: action.payload.title,
                  tags: action.payload.tags,
                })
            break
          case 'company':
            newState.session.user.companyId.videos = newState.session.user.companyId.videos
              .map(video => video._id !== action.payload.id
                ? video
                : {
                  ...video,
                  title: action.payload.title,
                  tags: action.payload.tags,
                })
            break
          default:
            break
        }

        break
      }

      default:
    }

    newState.opentok = opentokReducer(newState.opentok, action)

    return newState
  },

  ...modelReducers,

  ...formReducers,
}
